import React, { useState } from 'react';
import { CheckSquare, Users, UserCheck } from 'lucide-react';
import { Card, Input, Button } from '../ui';
import { cn } from '../../lib/utils';
import { useNodeRoster } from '../../hooks/useNodeRoster';
import { NodeRosterPickerModal } from './common/NodeRosterPickerModal';
import { DirectoryMember } from '../../types';
import toast from 'react-hot-toast';

export function QuorumAndRollChecker() {
  const [totalRoster, setTotalRoster] = useState('100');
  const [presentMembers, setPresentMembers] = useState('55');
  const [quorumPercentage, setQuorumPercentage] = useState('50');

  const { hasActiveNode, nodeName, members } = useNodeRoster();
  const [isRollCallOpen, setIsRollCallOpen] = useState(false);

  const total = parseInt(totalRoster) || 0;
  const present = parseInt(presentMembers) || 0;
  const thresh = parseFloat(quorumPercentage) || 0;

  const quorumRequiredNum = Math.ceil(total * (thresh / 100));
  const quorumAchieved = present >= quorumRequiredNum;

  const handleRollCallConfirm = (presentList: DirectoryMember[]) => {
    setPresentMembers(presentList.length.toString());
    setTotalRoster(members.length.toString());
    toast.success(`Roll call recorded: ${presentList.length} of ${members.length} members present.`);
  };

  const handleSyncTotal = () => {
    if (members.length > 0) {
      setTotalRoster(members.length.toString());
      toast.success(`Total voting roster set to ${members.length} active members from ${nodeName}.`);
    }
  };

  return (
    <div className="space-y-6">
      {hasActiveNode && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-[#FFD600]/20 border-2 border-black dark:border-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black text-white dark:bg-white dark:text-black flex items-center justify-center border border-black dark:border-white">
              <Users size={20} />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-tight text-black dark:text-white">
                {nodeName} Directory Connected
              </p>
              <p className="text-[10px] text-gray-600 dark:text-gray-400 font-bold">
                {members.length} voting members recorded in organization roster
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSyncTotal}
              className="rounded-none border-2 border-black dark:border-white text-xs font-mono font-bold uppercase"
            >
              Sync Total ({members.length})
            </Button>
            <Button
              size="sm"
              onClick={() => setIsRollCallOpen(true)}
              className="rounded-none bg-[#0044FF] text-white hover:bg-blue-700 text-xs font-mono font-bold uppercase border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000]"
            >
              <UserCheck size={14} className="mr-1.5" /> Take Roll Call
            </Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        <Card className="p-6 space-y-4 border-2 border-black dark:border-white border-gray-150">
          <h3 className="text-lg font-bold flex items-center gap-2 mb-2">
            <CheckSquare className="text-blue-500" size={20} /> Parameters
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Total Active Voting Roster</label>
                {hasActiveNode && (
                  <span className="text-[9px] font-mono text-[#0044FF] font-bold uppercase">Linked to {nodeName}</span>
                )}
              </div>
              <Input type="number" value={totalRoster} onChange={(e) => setTotalRoster(e.target.value)} />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Voting Members Present</label>
                {hasActiveNode && (
                  <button
                    type="button"
                    onClick={() => setIsRollCallOpen(true)}
                    className="text-[9px] font-mono text-blue-600 dark:text-blue-400 font-bold uppercase underline cursor-pointer"
                  >
                    Check Present Members
                  </button>
                )}
              </div>
              <Input type="number" value={presentMembers} onChange={(e) => setPresentMembers(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Bylaw Quorum Threshold (%)</label>
              <Input type="number" value={quorumPercentage} onChange={(e) => setQuorumPercentage(e.target.value)} />
            </div>
          </div>
        </Card>

      <Card className={cn(
        "p-8 flex flex-col justify-between border-2 border-black dark:border-white",
        quorumAchieved 
          ? "bg-green-50 text-green-900 dark:bg-green-950/10 dark:text-green-300" 
          : "bg-amber-50 text-amber-900 dark:bg-amber-950/10 dark:text-amber-300"
      )}>
        <div className="space-y-4">
          <span className="text-xs font-bold uppercase tracking-wider font-mono">Live Quorum Status</span>
          
          <div className="space-y-1">
            <h2 className="text-3xl font-black font-display flex items-center gap-2">
              {quorumAchieved ? 'QUORUM PRESENT' : 'NO QUORUM'}
            </h2>
            <p className="text-sm opacity-80 leading-relaxed">
              {quorumAchieved 
                ? 'The meeting is legally constituted. Business, elections, and binding resolutions may be transacted.'
                : `A minimum of ${quorumRequiredNum} voting members are required to transact official business.`
              }
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-black/10 dark:border-white/10">
            <div>
              <span className="text-[10px] uppercase font-mono block opacity-60">Required Present:</span>
              <span className="text-xl font-bold font-mono">{quorumRequiredNum} / {total}</span>
            </div>
            <div>
              <span className="text-[10px] uppercase font-mono block opacity-60">Variance:</span>
              <span className="text-xl font-bold font-mono">
                {present - quorumRequiredNum >= 0 ? `+${present - quorumRequiredNum}` : present - quorumRequiredNum}
              </span>
            </div>
          </div>
        </div>

        <div className="pt-6 font-mono text-[10px] opacity-75">
          Based on standard Robert's Rules of Order parliamentary recommendations.
        </div>
      </Card>
    </div>

    {hasActiveNode && (
      <NodeRosterPickerModal
        isOpen={isRollCallOpen}
        onClose={() => setIsRollCallOpen(false)}
        onImport={handleRollCallConfirm}
        title={`Conduct Roll Call: ${nodeName || 'Organization'}`}
        description={`Check off all members who are present in-person or remotely for this meeting.`}
        confirmLabel="Record Roll Call"
        allowEmpty={true}
      />
    )}
  </div>
  );
}
